import {
  DetailsTree,
  DetailsTreeItemValue,
  formatEnergyAmount,
  getMWhFromKWh,
} from "@synota-io/synota-shared-ui";
import { IDashboardContractDetails } from "../../../../api/hooks/useContractDetails";
import { Card, Typography } from "@mui/material";
import { useMemo } from "react";
import { delaySecondsToHours } from "../../../../utils/date";
import { SettlementDelayCell } from "../fields/SettlementDelayCell";

const SELECTORS_ROOT_ID = "contract_selectors";
const SOURCES_ROOT_ID = "contract_data_sources";

function requiredId(id: string | null | undefined, context: string): string {
  if (!id) {
    throw new Error(`Missing required id for ${context}`);
  }

  return id;
}

function emptyRoot(parentId: string): DetailsTreeItemValue[] {
  return [{ id: `${parentId}__empty`, label: "", render: () => "No data found." }];
}

function contractSourcesToTree(contract: IDashboardContractDetails): DetailsTreeItemValue[] {
  return [
    {
      id: SOURCES_ROOT_ID,
      label: "Data Sources",
      children: contract.data_sources?.length
        ? contract.data_sources.map((source, sourceIndex) => {
            const id = `${SOURCES_ROOT_ID}_source_${sourceIndex}`;
            return {
              id,
              label: source.purpose || source.reader_type,
              children: [
                {
                  id: `${id}_reader_type`,
                  label: "Reader Type",
                  value: source.reader_type,
                },
                ...source.configuration.map((config, configIndex) => ({
                  id: `${id}_configuration_${configIndex}`,
                  label: config.title,
                  value: config.value,
                })),
              ],
            };
          })
        : emptyRoot(SOURCES_ROOT_ID),
    },
  ];
}

function contractSelectorsToTree(contract: IDashboardContractDetails): DetailsTreeItemValue[] {
  return [
    {
      id: SELECTORS_ROOT_ID,
      label: "Data Prioritization",
      children: contract.selectors?.length
        ? contract.selectors.map((selector) => {
            const id = requiredId(selector.id, `selector "${selector.purpose}"`);
            return {
              id,
              label: selector.purpose,
              children: [
                {
                  id: `${id}_interval`,
                  label: "Interval",
                  value: selector.interval_length_in_seconds
                    ? delaySecondsToHours(selector.interval_length_in_seconds)
                    : "-",
                },
                {
                  id: `${id}_timezone`,
                  label: "Time Zone",
                  value: selector.time_zone || "-",
                },
                {
                  id: `${id}_has_alerts`,
                  label: "Email Alerts",
                  value: selector.has_notices ? "Yes" : "No",
                },
                ...selector.data_sources.map((source) => {
                  const sourceId = requiredId(
                    source.id,
                    `data source "${source.description}" for selector "${selector.purpose}"`,
                  );
                  return {
                    id: sourceId,
                    label: source.description,
                    children: [
                      {
                        id: `${sourceId}_range_requirements`,
                        label: "Range Requirements",
                        render: () => (
                          <Typography variant="caption" fontWeight={700}>
                            {source.require_above_threshold && source.require_below_threshold ? (
                              <span>
                                <span>Between </span>
                                <span>
                                  {formatEnergyAmount(
                                    getMWhFromKWh(Number(source.require_above_threshold)),
                                  )}
                                </span>
                                <span> and </span>
                                <span>
                                  {formatEnergyAmount(
                                    getMWhFromKWh(Number(source.require_below_threshold)),
                                  )}
                                </span>
                                <span> MWh/day</span>
                              </span>
                            ) : (
                              <span>
                                {source.require_above_threshold ? (
                                  <>
                                    <span>Above </span>
                                    <span>
                                      {formatEnergyAmount(
                                        getMWhFromKWh(Number(source.require_above_threshold)),
                                      )}
                                    </span>
                                    <span> MWh/day</span>
                                  </>
                                ) : source.require_below_threshold ? (
                                  <>
                                    <span>Below </span>
                                    <span>
                                      {formatEnergyAmount(
                                        getMWhFromKWh(Number(source.require_below_threshold)),
                                      )}
                                    </span>
                                    <span> MWh/day</span>
                                  </>
                                ) : (
                                  "-"
                                )}
                              </span>
                            )}
                          </Typography>
                        ),
                      },
                      {
                        id: `${sourceId}_settlement_delay`,
                        label: "Data Valid After",
                        render: () => (
                          <SettlementDelayCell
                            row={{
                              advanced_settlement_delay: source,
                              delay_in_seconds: source.delay_in_seconds,
                            }}
                          />
                        ),
                      },
                    ] as DetailsTreeItemValue[],
                  };
                }),
              ],
            };
          })
        : emptyRoot(SELECTORS_ROOT_ID),
    },
  ];
}

export const ContractSelectors = ({ contract }: { contract: IDashboardContractDetails }) => {
  const items: DetailsTreeItemValue[] = useMemo(
    () => contractSelectorsToTree(contract),
    [contract],
  );

  return (
    <Card sx={{ p: 1 }}>
      <DetailsTree defaultExpandedItems={[SELECTORS_ROOT_ID]} items={items} />
    </Card>
  );
};

export const ContractDataSources = ({ contract }: { contract: IDashboardContractDetails }) => {
  const items: DetailsTreeItemValue[] = useMemo(() => contractSourcesToTree(contract), [contract]);

  return (
    <Card sx={{ p: 1 }}>
      <DetailsTree defaultExpandedItems={[SOURCES_ROOT_ID]} items={items} />
    </Card>
  );
};
